import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { parseFunctionalTestingSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage functional testing versions
 * Stores and tracks different versions of functional tests from regenerations
 *
 * Uses useVersionManagementV1 which fixes:
 * - Local edit detection via isLocalUpdateRef
 * - Proper version tracking without creating duplicates on edit
 */
const useFunctionalTestingVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for functional testing version management
  const functionalTestingConfig = {
    // Determine if a message contains functional testing content
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      return content.includes('Functional Testing') ||
             content.includes('Test Case') ||
             content.includes('Test ID') ||
             content.includes('TC-') ||
             content.includes('User Flow') ||
             content.includes('###');
    },

    // Parse functional testing sections from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseFunctionalTestingSections(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagementV1 based on messageIndex
    extractVersions: (msg, parsedTestCases, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagementV1 with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        testCases: parsedTestCases,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the V1 version management hook that supports isLocalUpdateRef
  const versionData = useVersionManagementV1(functionalTestingConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      testCases: versionData.currentVersion.parsedContent || versionData.currentVersion.testCases
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to functional testing-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useFunctionalTestingVersions;
